import type { ArenaRoomMetadata } from '@arena/shared';
import type { RoomAvailable } from '@colyseus/sdk';
import { useEffect, useState } from 'react';
import { joinLobby, type LobbyRoom } from './client';

export type ListedRoom = RoomAvailable<ArenaRoomMetadata>;

/** Apply one LobbyRoom message to the current listing. */
export function applyLobbyMessage(
  rooms: ListedRoom[],
  type: 'rooms' | '+' | '-',
  payload: unknown,
): ListedRoom[] {
  switch (type) {
    case 'rooms':
      return payload as ListedRoom[];
    case '+': {
      const [roomId, room] = payload as [string, ListedRoom];
      const i = rooms.findIndex((r) => r.roomId === roomId);
      return i === -1 ? [...rooms, room] : rooms.map((r, j) => (j === i ? room : r));
    }
    case '-':
      return rooms.filter((r) => r.roomId !== payload);
  }
}

/** Live list of open arenas while the component is mounted (joins the `lobby` room). */
export function useRoomBrowser(): { rooms: ListedRoom[]; online: boolean } {
  const [rooms, setRooms] = useState<ListedRoom[]>([]);
  const [online, setOnline] = useState(false);

  useEffect(() => {
    let lobby: LobbyRoom | null = null;
    let cancelled = false;
    joinLobby()
      .then((room) => {
        if (cancelled) return void room.leave();
        lobby = room;
        setOnline(true);
        for (const type of ['rooms', '+', '-'] as const) {
          room.onMessage(type, (payload: unknown) =>
            setRooms((prev) => applyLobbyMessage(prev, type, payload)),
          );
        }
        room.onLeave(() => setOnline(false));
      })
      .catch(() => setOnline(false));
    return () => {
      cancelled = true;
      void lobby?.leave();
    };
  }, []);

  return { rooms, online };
}
